
  import {
    isOperatorCountingNightBackFlight,
    isOperatorTranslateCorrectNightsInHotel,
    isOperatorTranslateCorrectNightsInTour,
    calculateNightsInHotelByNightBackFlight,
    calculateNightsInHotelByFlight,
    calculateNightsInTourByFlight,
    type Leg as NightsLeg,
  } from '@travelata-libs/ui-shared';
  import { defineComponent, type PropType, computed, unref, toRefs } from 'vue';
  import { type Operator } from '@entities/operator';
  import { asyncSvgLoader, wordEnding } from '@shared/lib';
  import { IconBase } from '@shared/ui';
  import { type Leg, type CountryCondition } from '@ve/types';
  import iconNights from './img/nights.svg';

  const TIME_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/;

  const toUtcDate = (date: string): string => (date ? `${date.replace(' ', 'T').replace(TIME_ZONE, '')}Z` : date);

  const toNightsLegs = (legs: Leg[]): NightsLeg[] =>
    legs.map((leg) => ({
      departureDate: toUtcDate(leg.departureDate),
      arrivalDate: toUtcDate(leg.arrivalDate),
      airportDeparture: { timezone: 'UTC' },
      airportArrival: { timezone: 'UTC' },
    })) as NightsLeg[];

  export default defineComponent({
    components: {
      IconBase,
      iconNights: asyncSvgLoader(iconNights),
    },
    props: {
      nights: {
        type: Number,
        default: 0,
      },
      nightsInHotel: {
        type: Number,
        default: 0,
      },
      country: {
        type: Object as PropType<CountryCondition | null>,
        default: null,
      },
      operator: {
        type: Object as PropType<Operator | null>,
        default: null,
      },
      thereLegs: {
        type: Array as PropType<Leg[]>,
        default: () => [],
      },
      backLegs: {
        type: Array as PropType<Leg[]>,
        default: () => [],
      },
      smallText: {
        type: Boolean,
        default: false,
      },
      isHotelOffer: {
        type: Boolean,
        default: false,
      },
      tourPageFormat: {
        type: Boolean,
        default: false,
      },
    },
    setup(props) {
      const { nights, nightsInHotel, operator, isHotelOffer, country, thereLegs, backLegs, tourPageFormat } =
        toRefs(props);

      const countryId = computed(() => unref(country)?.id ?? 0);
      const hasRoute = computed(() => unref(thereLegs).length > 0 && unref(backLegs).length > 0);
      const nightsThereLegs = computed(() => toNightsLegs(unref(thereLegs)));
      const nightsBackLegs = computed(() => toNightsLegs(unref(backLegs)));

      const calculatedNightsInHotel = computed(() => {
        const operatorId = unref(operator)?.id;
        if (!operatorId || !unref(hasRoute)) return unref(nightsInHotel);

        if (isOperatorCountingNightBackFlight(operatorId)) {
          return calculateNightsInHotelByNightBackFlight(unref(nightsThereLegs), unref(nightsBackLegs));
        }

        if (!isOperatorTranslateCorrectNightsInHotel(operatorId, unref(countryId))) {
          return calculateNightsInHotelByFlight(unref(nightsThereLegs), unref(nightsBackLegs));
        }

        return unref(nightsInHotel);
      });

      const calculatedNightsInTour = computed(() => {
        const operatorId = unref(operator)?.id;
        if (!operatorId || !unref(hasRoute)) return unref(nights);

        return isOperatorTranslateCorrectNightsInTour(operatorId, unref(countryId))
          ? unref(nights)
          : calculateNightsInTourByFlight(unref(nightsThereLegs), unref(nightsBackLegs));
      });
      const isDifferenceNightByNightsInHotel = computed(
        () => unref(calculatedNightsInTour) !== unref(calculatedNightsInHotel),
      );

      const isDifferenceNightsOperatorByCalculatedNights = computed(
        () => unref(nights) !== unref(calculatedNightsInTour),
      );

      const nightsInHotelText = computed(() => {
        if (unref(isHotelOffer)) {
          return `на ${unref(nights)} ноч${wordEnding(unref(nights), 'ь;и;ей')} в отеле`;
        }
        if (unref(tourPageFormat)) {
          return `${unref(calculatedNightsInHotel)} в отеле`;
        }
        return `из них ${unref(calculatedNightsInHotel)} в отеле`;
      });

      return {
        isDifferenceNightByNightsInHotel,
        isDifferenceNightsOperatorByCalculatedNights,
        nightsInHotelText,
        calculatedNightsInHotel,
        calculatedNightsInTour,
      };
    },
    methods: {
      wordEnding,
    },
  });
